import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from './client';
import { queryKeys } from './queryKeys';
import type { Run, RunEventRequest } from '../types';

export function useRuns(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.runs(jobId),
    queryFn: jobId
      ? () => apiFetch<Run[]>(`/api/v1/jobs/${encodeURIComponent(jobId)}/runs`)
      : skipToken,
    placeholderData: keepPreviousData,
  });
}

// `useRun` no longer polls — the run page's `useRunSubscription` pushes live
// state over SSE. This stays a one-shot cache reader: it seeds the run's
// metadata once and is the surface where a cross-user 403 lands as a
// distinguishable error (the SSE stream can't surface 403 — see
// runSubscription.ts and a1ec CLAUDE.md §A1EE-323).
//
// No `keepPreviousData` here, deliberately: this is a single-entity reader, and
// keeping the previous run's data across a run→run navigation would render run
// A's id/state/failure notice under run B's URL (isPending stays false on
// placeholder data, so RunPage's loading branch never fires). List queries
// (`useRuns` above) keep theirs — prior rows during a key change is the point.
export function useRun(jobId: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.run(jobId, runId),
    queryFn:
      jobId && runId
        ? () =>
            apiFetch<Run>(
              `/api/v1/jobs/${encodeURIComponent(jobId)}/runs/${encodeURIComponent(runId)}`,
            )
        : skipToken,
  });
}

// Run-event mutation (A1EE-338). Currently only the Cancel event. Mirrors the
// api/admin.ts useMutation pattern; returns the updated Run. SSE refreshes run
// state too — the invalidate covers the polled cache reader the run page uses.
export function useDispatchRunEvent(jobId: string, runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: RunEventRequest) =>
      apiFetch<Run>(
        `/api/v1/jobs/${encodeURIComponent(jobId)}/runs/${encodeURIComponent(runId)}/events`,
        {
          method: 'POST',
          body: JSON.stringify(request),
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.run(jobId, runId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.runs(jobId) });
    },
  });
}
